
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StorageService } from './services/storage';
import { generateDailyLesson } from './services/gemini';
import { AppState, UserProfile, AppStatus, Lesson } from './types';
import { I18N, GOLD_DAYS } from './constants';
import { validateGoldCode } from './services/subscription';
import { getCurrentPlanStatus } from './services/subscriptionStatus';

// Components
import AuthView from './components/Auth';
import LessonView from './components/LessonView';
import PaywallView from './components/Paywall';
import SettingsView from './components/Settings';

const App: React.FC = () => {
  const hasAttemptedAutoGenerateRef = useRef(false);
  const [status, setStatus] = useState<AppStatus>(AppStatus.UNAUTHENTICATED);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [state, setState] = useState<AppState | null>(null);
  const [pin, setPin] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'home' | 'settings'>('home');
  const [lang, setLang] = useState<'fr' | 'en'>('en');

  // Detect Language
  useEffect(() => {
    const browserLang = navigator.language.split('-')[0];
    setLang(browserLang === 'fr' ? 'fr' : 'en');
  }, []);

  // Check Profile & Trial
  useEffect(() => {
    const savedProfile = StorageService.getProfile();
    if (savedProfile) {
      const subscriptionStatus = getCurrentPlanStatus(savedProfile);

      if (!subscriptionStatus.isActive) {
        setStatus(AppStatus.PAYWALL);
      }

      setProfile(savedProfile);
    }
  }, []);

  const handleLogin = async (inputPin: string) => {
    setPin(inputPin);
    setIsLoading(true);
    try {
      const savedProfile = StorageService.getProfile();
      if (!savedProfile) {
        // Initial setup
        const newProfile: UserProfile = {
          pin: inputPin,
          language: lang,
          firstLogin: new Date().toISOString(),
          subscription: {
            plan: 'FREE',
            startDate: new Date().toISOString(),
            expiresAt: null,
            isTrial: true
          }
        };
        StorageService.saveProfile(newProfile);
        setProfile(newProfile);
        
        const initialState: AppState = { lessons: [], profile: newProfile, currentLesson: null };
        await StorageService.saveState(initialState, inputPin);
        setState(initialState);
      } else {
        // Verify PIN by trying to decrypt state
        const decryptedState = await StorageService.getState(inputPin);
        if (decryptedState) {
          setState(decryptedState);
        } else {
          throw new Error("Invalid Pin");
        }
      }
      
      // Check subscription again after login
      const profileNow = StorageService.getProfile();
      if (profileNow) {
        const subscriptionStatus = getCurrentPlanStatus(profileNow);
        setStatus(subscriptionStatus.isActive ? AppStatus.AUTHENTICATED : AppStatus.PAYWALL);
      }
    } catch (e) {
      alert(I18N[lang].invalidPin);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewLesson = useCallback(async () => {
    if (!state || !pin) return;
    setIsLoading(true);
    try {
      const existingTitles = state.lessons.map(l => l.title);
      // Get the date of the most recent lesson to avoid duplicates on the same day
      const lastLessonDate = state.lessons[0]?.date;
      
      const lesson = await generateDailyLesson(lang, existingTitles, undefined, lastLessonDate);
      
      const newState = { 
        ...state, 
        lessons: [lesson, ...state.lessons].slice(0, 50),
        currentLesson: lesson 
      };
      setState(newState);
      await StorageService.saveState(newState, pin);
    } catch (e: any) {
      if (e.message === "ALREADY_GENERATED_TODAY") {
        alert(I18N[lang].alreadyGenerated);
      } else {
        console.error(e);
      }
    } finally {
      setIsLoading(false);
    }
  }, [lang, pin, state]);

  useEffect(() => {
    if (status !== AppStatus.AUTHENTICATED) return;
    if (!state || !pin) return;
    if (isLoading) return;
    if (hasAttemptedAutoGenerateRef.current) return;

    hasAttemptedAutoGenerateRef.current = true;

    const latestLessonDate = state.lessons[0]?.date;
    if (!latestLessonDate || new Date(latestLessonDate).toDateString() !== new Date().toDateString()) {
      void handleNewLesson();
    }
  }, [handleNewLesson, isLoading, pin, state, status]);

  const handleToggleFavorite = async (lessonId: string) => {
    if (!state || !pin) return;
    const toggle = (l: Lesson) => l.id === lessonId ? { ...l, isFavorite: !l.isFavorite } : l;
    const newState: AppState = {
      ...state,
      lessons: state.lessons.map(toggle),
      currentLesson: state.currentLesson ? toggle(state.currentLesson) : null
    };
    setState(newState);
    await StorageService.saveState(newState, pin);
  };

  const handleUpdateNotes = async (lessonId: string, notes: string) => {
    if (!state || !pin) return;
    const update = (l: Lesson) => l.id === lessonId ? { ...l, notes } : l;
    const newState: AppState = {
      ...state,
      lessons: state.lessons.map(update),
      currentLesson: state.currentLesson ? update(state.currentLesson) : null
    };
    setState(newState);
    await StorageService.saveState(newState, pin);
  };

  const handleUpgrade = (code: string) => {
    if (validateGoldCode(code)) {
      const expiry = new Date();
      expiry.setDate(expiry.getDate() + GOLD_DAYS);
      const updatedProfile: UserProfile = {
        ...profile!,
        subscription: {
          plan: 'GOLD',
          startDate: new Date().toISOString(),
          expiresAt: expiry.toISOString(),
          isTrial: false
        }
      };
      StorageService.saveProfile(updatedProfile);
      setProfile(updatedProfile);
      setStatus(AppStatus.AUTHENTICATED);
    } else {
      alert(I18N[lang].invalidGoldCode);
    }
  };

  const handleBackFromPaywall = () => {
    if (!profile) return;

    const subscriptionStatus = getCurrentPlanStatus(profile);

    if (subscriptionStatus.isActive) {
      setStatus(AppStatus.AUTHENTICATED);
      setActiveTab('home');
    } else {
      // If expired, they cannot leave the paywall
      alert(lang === 'fr' ? 'Votre abonnement a expiré. Veuillez activer le plan GOLD.' : 'Your subscription has expired. Please activate the GOLD plan.');
    }
  };

  const handleBackup = async () => {
    const data = await StorageService.exportData(pin);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omen_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  const handleRestore = async (file: File) => {
    const text = await file.text();
    StorageService.importData(text);
    window.location.reload();
  };

  const t = I18N[lang];
  const favorites = state?.lessons.filter(l => l.isFavorite) || [];

  return (
    <div className="min-h-screen max-w-md mx-auto bg-white flex flex-col relative overflow-x-hidden">
      {status === AppStatus.UNAUTHENTICATED && (
        <AuthView onLogin={handleLogin} isLoading={isLoading} t={t} isNewUser={!profile} />
      )}

      {status === AppStatus.PAYWALL && profile && (
        <PaywallView onUpgrade={handleUpgrade} onBack={handleBackFromPaywall} profile={profile} t={t} lang={lang} />
      )}

      {status === AppStatus.AUTHENTICATED && state && (
        <>
          <header className="p-4 bg-white border-b sticky top-0 z-10 flex justify-between items-center">
            <h1 className="text-xl font-bold gold-gradient bg-clip-text text-transparent">{t.welcome}</h1>
            <div className="flex gap-4 items-center">
               <button 
                 onClick={() => setStatus(AppStatus.PAYWALL)}
                 className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-widest ${profile?.subscription.plan === 'GOLD' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}
               >
                 {profile?.subscription.plan === 'GOLD' ? 'GOLD' : 'SILVER'}
               </button>
               <button onClick={() => setActiveTab('settings')} className="text-gray-400">
                 <i className={`fa-solid fa-cog ${activeTab === 'settings' ? 'text-yellow-600' : ''}`}></i>
               </button>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 pb-24">
            {activeTab === 'home' ? (
              <LessonView 
                lesson={state.currentLesson} 
                onGenerate={handleNewLesson} 
                onToggleFavorite={handleToggleFavorite}
                onUpdateNotes={handleUpdateNotes}
                isLoading={isLoading} 
                t={t} 
              />
            ) : (
              <SettingsView 
                onBackup={handleBackup} 
                onRestore={handleRestore} 
                favorites={favorites}
                onSelectLesson={(l) => {
                  setState({ ...state, currentLesson: l });
                  setActiveTab('home');
                }}
                profile={profile!}
                t={t} 
                onLogout={() => window.location.reload()}
              />
            )}
          </main>

          <nav className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t p-3 flex justify-around safe-area-bottom z-20">
            <button 
              onClick={() => setActiveTab('home')}
              className={`flex flex-col items-center gap-1 ${activeTab === 'home' ? 'text-yellow-600' : 'text-gray-400'}`}
            >
              <i className="fa-solid fa-house text-xl"></i>
              <span className="text-xs">{t.dailyLesson}</span>
            </button>
            <button 
              onClick={() => setActiveTab('settings')}
              className={`flex flex-col items-center gap-1 ${activeTab === 'settings' ? 'text-yellow-600' : 'text-gray-400'}`}
            >
              <i className="fa-solid fa-user text-xl"></i>
              <span className="text-xs">{t.settings}</span>
            </button>
          </nav>
        </>
      )}
    </div>
  );
};

export default App;
